// NFC road-case seam — the JS half of the CUSTOM CoreNFC Capacitor plugin `Nfc` (ios/App/App/NfcPlugin.swift,
// a port of the legacy app's NFCPlugin, ART-253). Accessed via `registerPlugin` so @cntrl/native needs no npm dep;
// every entry is a no-op / false off iOS native, mirroring live-activity.ts.
//
// TWO ways a sticker reaches the app, and only one needs this plugin:
//   1. Background tag reading — iOS itself reads a paired sticker's NDEF URL (`/scan?token=…`) when the phone is
//      unlocked and NOT in an NFC session, and opens it; AASA routes it into the app and the deep-link router lands
//      on `/scan`. No plugin involved.
//   2. In-app SCAN MODE — the crew picks a target state and taps case after case. That needs a READ SESSION
//      (`startReadSession` + the `tagFound` / `sessionEnded` events), which is this plugin. WRITING a blank sticker
//      while pairing is the plugin's other job.
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

/** Why a read session ended — the Swift side's `sessionEnded.reason`. `tag-read` is the normal one-tag end
 * (scan mode restarts it); the rest stop or pause scan mode. */
export type NfcSessionEndReason = "tag-read" | "user-canceled" | "session-timeout" | "session-terminated" | "error";

interface NfcPlugin {
  /** Write a URL record to a blank NFC tag held to the phone (opens the system NFC sheet). */
  writeUrl(options: { url: string }): Promise<void>;
  /** Begin a one-tag NDEF read session; resolves once the sheet is up. The tag arrives as a `tagFound` event. */
  startReadSession(options: { promptMessage: string }): Promise<void>;
  /** Dismiss an open read session (no-op when none is open). */
  endReadSession(): Promise<void>;
  addListener(event: "tagFound", cb: (e: { url: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: "sessionEnded", cb: (e: { reason: NfcSessionEndReason; message?: string }) => void): Promise<PluginListenerHandle>;
}

interface HapticsPlugin {
  impact(options: { style: "LIGHT" | "MEDIUM" | "HEAVY" }): Promise<void>;
}

const Nfc = registerPlugin<NfcPlugin>("Nfc");
// @capacitor/haptics ships in the iOS wrapper; reached by name so this package takes no dependency on it.
const Haptics = registerPlugin<HapticsPlugin>("Haptics");

/**
 * True iff the native CoreNFC plugin is present (an iOS build with `Nfc` registered). Gates BOTH in-app NFC
 * affordances — writing a sticker and scan mode. Web and a build without the plugin → false, and the UI says so
 * honestly rather than offering a control that can only fail (NFC is iOS-app-only, the legacy rewrite ruling).
 */
export function nfcAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Nfc");
}

/** Write a `/scan?token=` URL onto a blank sticker. Rejects when NFC is unavailable — gate on {@link nfcAvailable}. */
export async function nfcWriteUrl(url: string): Promise<void> {
  if (!nfcAvailable()) throw new Error("NFC writing only works in the Artist CNTRL iOS app.");
  await Nfc.writeUrl({ url });
}

/** Open a one-tag read session with `promptMessage` on the system sheet. */
export async function nfcStartReadSession(promptMessage: string): Promise<void> {
  if (!nfcAvailable()) throw new Error("NFC scanning only works in the Artist CNTRL iOS app.");
  await Nfc.startReadSession({ promptMessage });
}

/** Close any open read session. Never throws — it is called on teardown. */
export async function nfcEndReadSession(): Promise<void> {
  if (!nfcAvailable()) return;
  try {
    await Nfc.endReadSession();
  } catch {
    /* already closed */
  }
}

/** Subscribe to tags read in a session. Returns an unsubscribe. */
export async function onNfcTagFound(cb: (url: string) => void): Promise<() => void> {
  if (!nfcAvailable()) return () => {};
  const h = await Nfc.addListener("tagFound", (e) => cb(e.url ?? ""));
  return () => void h.remove();
}

/** Subscribe to read-session ends. Returns an unsubscribe. */
export async function onNfcSessionEnded(
  cb: (reason: NfcSessionEndReason, message?: string) => void,
): Promise<() => void> {
  if (!nfcAvailable()) return () => {};
  const h = await Nfc.addListener("sessionEnded", (e) => cb(e.reason, e.message));
  return () => void h.remove();
}

/** A single haptic tick for a successful scan. No-op off native or without the Haptics plugin. */
export function nfcScanHaptic(): void {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("Haptics")) return;
  void Haptics.impact({ style: "MEDIUM" }).catch(() => {});
}

/** The sticker token out of a scanned `/scan?token=…` URL, or null. Pure — the one parser for both scan paths. */
export function scanTokenFromUrl(url: string): string | null {
  const m = /[?&]token=([^&#]+)/.exec(url);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}
